














import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { ElementActionsKeys, ElementActionsPayload } from '../../store/types/element';
import DrawingElement from './Elements/DrawingElement.vue';

@Component({
  components: {
    DrawingElement,
  },
})
export default class Drawing extends Vue {
  @Prop(Boolean) drawing!: boolean;
  @Prop(Array) intialPosition!: number[];
  @Prop(String) activeDrawingColor!: string;
  @Prop(String) activeDrawingThickness!: string;
  @Prop(Number) getNavOffset!: number;
  @Prop(Function) calculateMouseWithOffset!: (x: number, y: number) => { x: number; y: number };
  @Action(ElementActionsKeys.ADD_ELEMENT) addElement!: (payload: ElementActionsPayload['ADD_ELEMENT']) => void;

  isDrawing = this.drawing;

  @Watch('drawing')
  setDrawing(newVal: boolean) {
    if (!newVal) {
      this.mouseUp();
    } else {
      this.mouseDown();
    }
  }

  mousePosition = [0, 0];
  freeHandPath: string[] = [];
  bufferZone: string[] = [];
  bufferLength = 4;

  wrapperDisplay = 'block';

  get styleWrapper() {
    return {
      transform: `translate3d(${this.paths.length ? this.paths[0].initPos[0] : this.intialPosition[0]}px, ${
        this.paths.length ? this.paths[0].initPos[1] : this.intialPosition[1]
      }px, 0)`,
      display: this.wrapperDisplay,
    };
  }

  get path() {
    return [`M${this.offset[0]} ${this.offset[1]} `, ...this.freeHandPath].join(' ');
  }

  get allPaths() {
    return this.paths.map(p => p.path).join(' ') + ' ' + this.path;
  }

  mounted() {
    window.addEventListener('mousemove', this.setMousePosition);
    window.addEventListener('touchmove', this.setMousePosition);
  }

  beforeDestroy() {
    window.removeEventListener('mousemove', this.setMousePosition);
    window.removeEventListener('touchmove', this.setMousePosition);
  }

  paths: Array<{ path: string; initPos: number[] }> = [];

  timeout: NodeJS.Timeout | null = null;

  mouseUp() {
    this.freeHandPath.push(...this.bufferZone);
    if (this.shouldRenderPath(false)) {
      this.paths.push({
        path: this.path,
        initPos: this.intialPosition,
      });
    }
    this.freeHandPath = [];
    this.bufferZone = [];
    this.timeout = setTimeout(this.finishDrawing, 1000);
  }

  offset: number[] = [0, 0];

  mouseDown() {
    this.isDrawing = true;
    if (this.timeout !== null) clearTimeout(this.timeout);
    if (this.paths.length) {
      this.offset = [
        this.intialPosition[0] - this.paths[0].initPos[0],
        this.intialPosition[1] - this.paths[0].initPos[1],
      ];
    } else {
      this.offset = [0, 0];
    }
  }

  shouldRenderPath(all: boolean) {
    if (!this.path.includes('L') && !this.paths.length) return false;
    const { width, height } = this.calculatePositions(all);
    return width > 3 || height > 3;
  }

  calculatePositions(all: boolean) {
    const path = all ? this.allPaths : this.path;
    const pathVerts = path
      .split('M')
      .join('')
      .split(' L')
      .map(v => v.split(' '));

    const x = pathVerts.map(v => Number(v[0]));
    const y = pathVerts.map(v => Number(v[1]));
    const minX = Math.min(...x);
    const maxX = Math.max(...x);
    const minY = Math.min(...y);
    const maxY = Math.max(...y);

    const width = Math.abs(minX) + maxX;
    const height = Math.abs(minY) + maxY;
    return {
      minX,
      maxX,
      minY,
      maxY,
      width,
      height,
    };
  }

  @Watch('activeDrawingColor')
  @Watch('activeDrawingThickness')
  finishDrawing() {
    if (this.timeout !== null) clearTimeout(this.timeout);
    this.isDrawing = false;
    if (this.shouldRenderPath(true)) {
      const { minX, minY, width, height } = this.calculatePositions(true);
      this.addElement({
        type: 'Drawing',
        x: this.paths[0].initPos[0] + minX - 6,
        y: this.paths[0].initPos[1] + minY - 6,
        width: width + 12,
        height: height + 12,
        rotation: 0,
        data: {
          path: this.allPaths,
          viewBox: `${minX - 6} ${minY - 6} ${Math.max(width + 12, 1)} ${Math.max(height + 12, 1)}`,
          color: this.activeDrawingColor,
          thickness: this.activeDrawingThickness,
        },
        deleted: false,
      });
    }
    this.paths = [];
  }

  isTouchEvent(e: TouchEvent | MouseEvent): e is TouchEvent {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(e as any).touches;
  }

  async setMousePosition(e: TouchEvent | MouseEvent) {
    let pos: Touch | MouseEvent;
    if (this.isTouchEvent(e)) {
      pos = e.touches[0];
    } else {
      pos = e;
    }
    this.mousePosition = [pos.clientX, pos.clientY];

    if (this.drawing) {
      const mouseX = this.mousePosition[0] - this.getNavOffset;
      const mouseY = this.mousePosition[1];

      const { x, y } = this.calculateMouseWithOffset(mouseX, mouseY);
      this.bufferZone.push(
        `L${Number(x - this.intialPosition[0] + this.offset[0]).toFixed(4)} ${Number(
          y - this.intialPosition[1] + this.offset[1]
        ).toFixed(4)}`
      );
      if (this.bufferZone.length >= this.bufferLength) {
        this.freeHandPath.push(...this.bufferZone);
        const wrapper = this.$refs.drawingWrapper as HTMLElement;

        this.wrapperDisplay = 'none';
        wrapper.style.display = this.wrapperDisplay;
        wrapper.offsetHeight; // no need to store this anywhere, the reference is enough
        await this.$nextTick;
        this.wrapperDisplay = 'block';
        wrapper.style.display = this.wrapperDisplay;
        this.bufferZone = [];
      }
    }
  }
}
